import { useEffect, useState } from 'react';

import { PendingSocialLink, connectPendingSocial } from '@/auth';
import { waitForMfaGateToLift } from '@/auth/utils/cognitoLogin';
import { acceptInvitationAfterLogin } from '@/auth/utils/invitationAfterLogin';
import Button from '@/componentsv2/Button';

import EnrollOtpAuth from './EnrollOtpAuth';

interface CognitoMfaSetupProps {
  email: string;
  /** Where to resume once the api lets the session through. */
  next: string;
  /** True when TOTP is already enrolled and only the api's gate is pending. */
  enrolled?: boolean;
  /** The sign-in session lapsed before enrolment finished. */
  onExpired: () => void;
  /** ENG-6311: a provider to connect once the gate lifts, as a sign-in without enrolment does. */
  pendingSocialLink?: PendingSocialLink;
}

type Status = 'enrol' | 'finishing' | 'stalled';

/**
 * ENG-6103: TOTP enrolment for a Cognito user the api's MFA gate refuses,
 * then a wait for the gate to open (the api caches it for up to a minute),
 * then a full reload at `next` so every query and the websocket start over.
 */
export default function CognitoMfaSetup({
  email,
  next,
  enrolled = false,
  onExpired,
  pendingSocialLink,
}: CognitoMfaSetupProps): JSX.Element {
  const [status, setStatus] = useState<Status>(enrolled ? 'finishing' : 'enrol');

  useEffect(() => {
    if (status !== 'finishing') return undefined;
    let live = true;
    waitForMfaGateToLift().then(async (lifted) => {
      if (!live) return;
      // ENG-6105: the password step stopped at the gate, before the cached
      // invitation was accepted.
      if (!lifted) {
        setStatus('stalled');
        return;
      }
      if (pendingSocialLink) {
        await connectPendingSocial(pendingSocialLink, email);
        // Left while connecting: the cached invitation stays for the next sign-in.
        if (!live) return;
      }
      const destination = await acceptInvitationAfterLogin(next);
      // The user may have left while the invitation was pending.
      if (live) window.location.assign(destination);
    });
    return () => {
      live = false;
    };
  }, [status, next, pendingSocialLink, email]);

  if (status === 'enrol') {
    return (
      <EnrollOtpAuth
        email={email}
        setOtp={() => undefined}
        onCognitoEnrolled={() => setStatus('finishing')}
        onCognitoExpired={onExpired}
      />
    );
  }
  return (
    <div className="mx-auto mt-24 flex max-w-[420px] flex-col gap-16 text-center text-on-surface">
      <h1 className="text-24 font-bold">Two-factor authentication is set up</h1>
      {status === 'finishing' ? (
        <p>Finishing setup. This can take up to a minute.</p>
      ) : (
        <>
          <p>This is taking longer than usual. Please try again in a minute.</p>
          <Button onClick={() => setStatus('finishing')}>Try again</Button>
        </>
      )}
    </div>
  );
}
